import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Image, ImageBackground, StyleSheet, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import WebView from 'react-native-webview';
import { AdminResponseDto } from '../../types/AdminResponseDto';
import { AdminResponseStorage } from '../../storage/AdminResponseStorage';
import { AdminAssessmentDto, AdminReportSectionDto, AdminAssessmentReportDto } from '../../types/adminAssessment';
import { AppProfilePHIDto } from '../../types/api/appProfile';
import type { PatinfoHistoryDto } from '../../types/api/patinfo';
import { useUserStore } from '../../contexts/StoreContext';
import { hasStoredPractitionerSession, withPractitionerAuth } from '../../services/auth/practitionerSession';
import { resolveSkinChannelInfo, type SkinChannelInfo } from '../../services/skin/resolveSkinChannelInfo';
import { resolveTrendHistoryFromEmbedded, resolveTrendHistoryFromLocal } from '../../services/clients/ClientReportAssessments';
import { APP_CONSTANTS } from '../../config/constants';
import { loadPdfIconSrc } from '../../utils/reportPdfIcon';
import {
  generateReportSectionHtml,
  generateFullReportHtml,
  getAllReportSections,
  getAllReportSectionsWithSubReport,
  getReportHubConfig,
  parseTrendChartPreloads,
  buildTrendChartOverrides,
  wrapReportHtml,
  WEBVIEW_AUTO_HEIGHT_JS,
} from '../../utils/reportRenderer';

// Question-page header — mirrors AdminView.tsx's own QUESTION_HEADER_HEIGHT/
// QUESTION_HEADER_LOGO_HEIGHT constants (not exported there) so the report page's skin
// header matches the assessment page's exactly.
const QUESTION_HEADER_HEIGHT = 90;
const QUESTION_HEADER_LOGO_HEIGHT = 65;

interface ReportViewProps {
  profileId?: string;
  recId?: string; // The specific assessment record to display
  assessmentDefinition?: AdminAssessmentDto; // The matched assessment definition
  storage?: any; // Storage interface
  showDebugInfo?: boolean; // Show debug information including response data
  enableEditReportLogic?: boolean; // Enable report logic editing
  profilePHI?: AppProfilePHIDto; // Optional PHI data for the current profile
  apiService?: any; // ApiService instance for backend saves
  accountId?: string; // Account/family ID for loading profile connections
  onBack?: () => void; // Callback to go back to previous view
  onViewPdf?: (html: string, filename: string) => void; // Web-only: open report as PDF in new tab
  /**
   * Pre-loaded AdminResponseDto to use instead of self-loading from local AdminResponseStorage
   * by profileId+recId — needed for records that were never administered on this device (e.g.
   * a profile browsed via Clients search, whose history was embedded directly in
   * PatinfoHistoryDto.Admin rather than fetched from local storage). When provided, the normal
   * self-load lookup is skipped entirely.
   */
  initialAdminResponse?: AdminResponseDto | null;
  /**
   * When provided, only the report whose reportId matches is shown (the output cards loop
   * below filters to it) — used when a caller already knows exactly which of the assessment's
   * reports[] the respondent should see (e.g. AdminView's renderReport PreScript flag, keyed
   * by AdminAssessmentReportDto.reportId — NOT dName, which is just a display label and isn't
   * guaranteed unique/stable), rather than every report in the assessment. Omitted/undefined
   * preserves the default behavior of showing every report.
   */
  selectedReportId?: string;
  /** True when mounted under a respondent/secure-token session (the /secure, /link, /remote
   * token-based flow) rather than a practitioner's own SRA login — mirrors AdminView's prop of
   * the same name. Gates the debug report-script editor behind a still-recoverable
   * practitioner session in the same origin storage, and routes its Save calls through that
   * practitioner's own session instead of the respondent's (see
   * services/auth/practitionerSession.ts) — the backend correctly rejects report_logic_put
   * authorized with a respondent/token session. */
  isRespondentSession?: boolean;
  /** Drives the skin branding header (logo/background) — same resolveSkinChannelInfo lookup
   * AdminView uses for its own question-page header, so a report reached from an assessment
   * shows the same branding. Defaults to APP_CONSTANTS.DEFAULT_SKIN_TYPE, matching AdminView. */
  skinType?: string;
  /** Label for the single footer exit button (styled like AdminView's enableDone/Continue
   * button) that calls onBack when pressed. Defaults to 'Back' for the existing "return to
   * profile/list" callers; SecureAdministrationView's immediate-feedback report screen passes
   * 'Continue' since onBack there proceeds to completeUrl rather than navigating back. */
  footerButtonLabel?: string;
  /**
   * The profile's full embedded assessment history (PatinfoProfileDto.history) — unfiltered,
   * any order, every assessmentId mixed together. Used only to precompute
   * enableBarGraph/enableLineGraph trend chart data (see reportRenderer.ts's
   * parseTrendChartPreloads/buildTrendChartOverrides and ClientReportAssessments.ts's
   * resolveTrendHistoryFromEmbedded). When omitted, ReportView falls back to its own local
   * AdminResponseStorage-derived history (the self-load flow below) — e.g. WellVisitView/
   * respondent sessions, which have no embedded profile history to pass in.
   */
  assessmentHistory?: PatinfoHistoryDto[];
}

export const ReportView: React.FC<ReportViewProps> = ({
  profileId,
  recId,
  assessmentDefinition,
  storage,
  showDebugInfo = false,
  enableEditReportLogic = false,
  profilePHI,
  apiService,
  accountId,
  onBack,
  onViewPdf,
  initialAdminResponse,
  selectedReportId,
  isRespondentSession = false,
  skinType = APP_CONSTANTS.DEFAULT_SKIN_TYPE,
  assessmentHistory,
  footerButtonLabel = 'Back',
}) => {
  const userStoreHook = useUserStore();
  const [adminResponse, setAdminResponse] = useState<AdminResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingScripts, setEditingScripts] = useState<{[key: string]: string}>({});
  const [savingScripts, setSavingScripts] = useState<{[key: string]: boolean}>({});
  const [scriptDirtyFlags, setScriptDirtyFlags] = useState<{[key: string]: boolean}>({});
  const [saveScriptResults, setSaveScriptResults] = useState<{[key: string]: {success: boolean, message: string} | null}>({});
  const [fullReportOutputs, setFullReportOutputs] = useState<{[key: string]: string}>({});
  const [isGeneratingReport, setIsGeneratingReport] = useState<{[key: string]: boolean}>({});
  const [webViewHeights, setWebViewHeights] = useState<{[key: string]: number}>({});
  // Tracks which reportSectionIds have local edits not yet pushed to the backend
  const [localDirtyFlags, setLocalDirtyFlags] = useState<{[reportSectionId: string]: boolean}>({});
  // Every locally-stored AdminResponseDto for this profile (all assessments mixed) — populated
  // either by the self-load effect below (when it already fetches this to find one record by
  // recId) or lazily by handleGenerateFullReport's trend-chart precompute when assessmentHistory
  // wasn't supplied (e.g. initialAdminResponse callers with no embedded profile history). A ref,
  // not state, since it's read-cache only — nothing here should trigger a re-render.
  const localProfileResponsesRef = useRef<AdminResponseDto[] | null>(null);

  // Every value a report script has putData()'d, across every report/section generated so far
  // — e.g. overrideRWidth (page width, mirroring AdminView's overrideQWidth) and any report-
  // scoped scratch value a script wants to set once and re-read via getData() (a base64 icon
  // reused across sections, say). Merged in (never wholesale-replaced) by handleGenerateFullReport
  // so one report's regeneration can't blank out values another report set — see there, and the
  // Debug Information "Report Data (putData)" section below for where this actually surfaces.
  const [reportDataOverrides, setReportDataOverrides] = useState<Record<string, string>>({});

  // Hub-report navigation (TestDrive / demo report — see getReportHubConfig). activeChildReportId
  // is whichever child report is currently open (null = on the hub screen); viewedChildReportIds
  // tracks which children have been opened at least once, which gates the hub's continue button.
  const [activeChildReportId, setActiveChildReportId] = useState<string | null>(null);
  const [viewedChildReportIds, setViewedChildReportIds] = useState<Set<string>>(() => new Set());
  // Widest measured hub nav button — applied to all of them so buttons with different-length
  // labels ("View Clinician Report" vs "View Client Report") render the same width. Only ever
  // grows (Math.max), so it settles after the first layout pass.
  const [hubButtonWidth, setHubButtonWidth] = useState<number | undefined>(undefined);

  // Skin branding (logo/background/bg color) for the page header — identical lookup/rendering
  // to AdminView.tsx's own renderSkinHeader, so a report reached from an assessment shows the
  // same branding as the question pages that led to it.
  const [skinChannelInfo, setSkinChannelInfo] = useState<SkinChannelInfo | null>(null);
  useEffect(() => {
    if (!storage || !skinType) {
      return;
    }
    let cancelled = false;
    void resolveSkinChannelInfo(storage, skinType).then((resolved) => {
      if (!cancelled) {
        setSkinChannelInfo(resolved);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storage, skinType]);

  // Skin's enablereportpdf: the PDF action lives in the report header ("View" + PDF icon beside
  // Back) instead of the Clients report columns. Needs onViewPdf, which only web callers supply
  // (ProGroupsWeb) — without it there's no way to open a PDF, so the action stays hidden.
  const showPdfAction = !!skinChannelInfo?.enableReportPdf && !!onViewPdf;
  const [pdfIconSrc, setPdfIconSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!showPdfAction || !storage) {
      return;
    }
    let cancelled = false;
    void loadPdfIconSrc(storage).then((src) => {
      if (!cancelled) {
        setPdfIconSrc(src);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [showPdfAction, storage]);

  // RN Image needs an explicit numeric width — measure the logo's natural aspect ratio and
  // scale width to match the fixed header logo height (same approach as AdminView).
  const [skinLogoWidth, setSkinLogoWidth] = useState<number | null>(null);
  useEffect(() => {
    const logoSrc = skinChannelInfo?.logoSrc;
    if (!logoSrc) {
      setSkinLogoWidth(null);
      return;
    }
    let cancelled = false;
    Image.getSize(
      logoSrc,
      (naturalWidth, naturalHeight) => {
        if (!cancelled && naturalHeight > 0) {
          setSkinLogoWidth((naturalWidth / naturalHeight) * QUESTION_HEADER_LOGO_HEIGHT);
        }
      },
      (error) => {
        console.warn('ReportView: failed to measure skin header logo size', error);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [skinChannelInfo?.logoSrc]);

  const renderSkinHeader = () => {
    const skinLogo = skinChannelInfo?.logoSrc ? (
      <Image
        source={{ uri: skinChannelInfo.logoSrc }}
        style={[styles.skinHeaderLogo, skinLogoWidth ? { width: skinLogoWidth } : null]}
        resizeMode="contain"
      />
    ) : null;

    return (
      <View
        style={[
          styles.skinHeaderShell,
          skinChannelInfo?.backgroundSrc
            ? { backgroundColor: 'transparent' }
            : skinChannelInfo?.skinBgColor
            ? { backgroundColor: skinChannelInfo.skinBgColor }
            : null,
        ]}
      >
        {skinChannelInfo?.backgroundSrc ? (
          <ImageBackground
            source={{ uri: skinChannelInfo.backgroundSrc }}
            style={styles.skinHeaderBackgroundImage}
            resizeMode="stretch"
          >
            {skinLogo}
          </ImageBackground>
        ) : (
          skinLogo
        )}
      </View>
    );
  };

  // Ensures auto-generation fires only once per mount, not on every re-render
  const autoGenerated = useRef(false);

  // Local debug flags — read directly from storage on mount so changes made in DebugView
  // are reflected immediately even when WellVisitView was already mounted.
  const [localEnableEditReportLogic, setLocalEnableEditReportLogic] = useState(enableEditReportLogic);
  const [localShowDebugInfo, setLocalShowDebugInfo] = useState(showDebugInfo);

  useEffect(() => {
    const loadDebugSettings = async () => {
      if (!storage) return;
      try {
        const editReportLogicState = await storage.getItem('debug_edit_report_logic');
        const wantsReportLogicEdit = editReportLogicState === 'true';

        // In a respondent/secure-token session this flag was written by an unrelated
        // practitioner tab sharing the same origin storage — only honor it here if that
        // practitioner's own session is still actually recoverable, so a genuine standalone
        // /secure link (or one whose practitioner tab has since logged out) never shows a
        // debug editor it can't save from. Mirrors AdminView's identical allowEdit gate.
        const allowEdit = !isRespondentSession || (wantsReportLogicEdit ? await hasStoredPractitionerSession(storage) : false);

        setLocalEnableEditReportLogic(wantsReportLogicEdit && allowEdit);

        const debugInfoState = await storage.getItem('debug_show_info');
        setLocalShowDebugInfo(debugInfoState === 'true');

        console.log('📊 ReportView: Loaded debug settings from storage:', {
          editReportLogicState,
          debugInfoState,
        });
      } catch (err) {
        console.error('📊 ReportView: Failed to load debug settings:', err);
      }
    };
    loadDebugSettings();
  }, [storage, isRespondentSession]);

  useEffect(() => {
    const loadAdminResponse = async () => {
      if (initialAdminResponse) {
        setAdminResponse(initialAdminResponse);
        setLoading(false);
        return;
      }

      if (!storage || !profileId || !recId) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      try {
        console.log('📊 ReportView: Loading admin response for:', { 
          profileId, 
          recId, 
          hasAssessmentDefinition: !!assessmentDefinition,
          assessmentName: assessmentDefinition?.dName,
          enableEditReportLogic: localEnableEditReportLogic,
          showDebugInfo: localShowDebugInfo
        });
        const adminResponseStorage = new AdminResponseStorage(storage);
        const profileResponses = await adminResponseStorage.getProfileResponses(profileId);
        // Reused by handleGenerateFullReport's trend-chart precompute so it doesn't need a
        // second fetch for this same on-device self-load flow.
        localProfileResponsesRef.current = profileResponses.responses;

        // Find the specific response by recId
        const response = profileResponses.responses.find(r => r.recId === recId);
        
        if (response) {
          console.log('📊 ReportView: Found admin response:', {
            recId: response.recId,
            recSurvey: response.recSurvey,
            recAlias: response.recAlias,
            recUTM: response.recUTM,
            responseDataCount: response.responseData.length
          });
          setAdminResponse(response);
        } else {
          setError(`Assessment report not found for RecId: ${recId}`);
        }
      } catch (err) {
        console.error('❌ ReportView: Failed to load admin response:', err);
        setError('Failed to load assessment report');
      } finally {
        setLoading(false);
      }
    };

    loadAdminResponse();
  }, [storage, profileId, recId, assessmentDefinition, initialAdminResponse]);

  // Load persisted report script edits from local storage on mount
  useEffect(() => {
    const loadStoredScripts = async () => {
      if (!storage || !assessmentDefinition?.surveyId) return;
      try {
        const key = getReportScriptStorageKey(assessmentDefinition.surveyId);
        const raw = await storage.getItem(key);
        if (!raw) return;
        const stored: Record<string, { script: string; dirty: boolean }> = JSON.parse(raw);
        const scripts: Record<string, string> = {};
        const dirtyMap: Record<string, boolean> = {};
        Object.entries(stored).forEach(([id, entry]) => {
          scripts[id] = entry.script;
          dirtyMap[id] = entry.dirty;
        });
        setEditingScripts(prev => ({ ...prev, ...scripts }));
        setLocalDirtyFlags(dirtyMap);
      } catch (err) {
        console.error('📝 ReportView: Failed to load stored report scripts:', err);
      }
    };
    loadStoredScripts();
  }, [storage, assessmentDefinition?.surveyId]);

  // Auto-generate all reports once on initial load — runs when adminResponse becomes available.
  // The Generate Report button remains available to recompute after script edits.
  useEffect(() => {
    if (autoGenerated.current || !adminResponse || !assessmentDefinition?.reports) return;
    autoGenerated.current = true;
    assessmentDefinition.reports.forEach((report, reportIndex) => {
      handleGenerateFullReport(report, String(reportIndex));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminResponse, assessmentDefinition]);

  // ── Report script local-storage helpers ────────────────────────────────────

  const getReportScriptStorageKey = (surveyId: string) =>
    `report_script_edits_${surveyId}`;

  const persistReportScriptEdit = async (
    reportSectionId: string,
    script: string,
    dirty: boolean
  ) => {
    if (!storage || !assessmentDefinition?.surveyId) return;
    try {
      const key = getReportScriptStorageKey(assessmentDefinition.surveyId);
      const raw = await storage.getItem(key);
      const stored: Record<string, { script: string; dirty: boolean; timestamp: string }> =
        raw ? JSON.parse(raw) : {};
      stored[reportSectionId] = { script, dirty, timestamp: new Date().toISOString() };
      await storage.setItem(key, JSON.stringify(stored));
    } catch (err) {
      console.error('📝 ReportView: Failed to persist report script edit:', err);
    }
  };

  const clearReportScriptDirtyInStorage = async (reportSectionId: string) => {
    if (!storage || !assessmentDefinition?.surveyId) return;
    try {
      const key = getReportScriptStorageKey(assessmentDefinition.surveyId);
      const raw = await storage.getItem(key);
      if (!raw) return;
      const stored: Record<string, { script: string; dirty: boolean; timestamp: string }> =
        JSON.parse(raw);
      if (stored[reportSectionId]) {
        stored[reportSectionId].dirty = false;
        await storage.setItem(key, JSON.stringify(stored));
      }
    } catch (err) {
      console.error('📝 ReportView: Failed to clear dirty flag in storage:', err);
    }
  };

  // ── End helpers ─────────────────────────────────────────────────────────────

  // Thin wrapper kept for backward compat with any call sites inside this component.
  // Delegates to the extracted shared utility. Dead/unused today (no call sites in this file)
  // — passes no reportPreloads since it has no parent report in scope to source them from.
  const generateReport = (
    reportSection: AdminReportSectionDto,
    responseData: AdminResponseDto,
    phi?: AppProfilePHIDto
  ): string => {
    return generateReportSectionHtml(reportSection, undefined, responseData, phi ?? profilePHI);
  };

  // Delegate to the shared wrapReportHtml utility.
  const wrapHtml = (html: string): string => wrapReportHtml(html);

  // Injected JS: posts document scroll height back to React Native so the WebView
  // can auto-size within the parent ScrollView. Shared with AggReportView.tsx via
  // reportRenderer.ts's WEBVIEW_AUTO_HEIGHT_JS.
  const webViewAutoHeightJS = WEBVIEW_AUTO_HEIGHT_JS;

  // Strip basic HTML tags for display in React Native (fallback, not used for main output)
  const stripHtml = (html: string): string => {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li>/gi, '• ')
      .replace(/<[^>]+>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Delegates to getAllReportSections from shared utility.
  const getAllSectionsForReport = (report: AdminAssessmentReportDto): AdminReportSectionDto[] =>
    getAllReportSections(report);

  // Delegates to getAllReportSectionsWithSubReport from shared utility.
  const getAllSectionsWithSubReport = (
    report: AdminAssessmentReportDto
  ): { subReportName: string; section: AdminReportSectionDto }[] =>
    getAllReportSectionsWithSubReport(report);

  // The response's own PHI snapshot (captured off AdminStateDto.phi at the moment this
  // administration completed) wins over the profilePHI prop, which just reflects whatever the
  // caller currently has on hand — that can be blank or stale (e.g. a /link token session's
  // profilePHI is built from the original launch PHI, typically empty; the real PHI only lands
  // in adminState.phi once the respondent fills out PhiCollectionForm mid-session). Used both
  // for actual report generation (getPHI() below) and the Debug Information PHI Data display,
  // so the two can't drift apart the way they did before this was hoisted out.
  const effectivePhi = adminResponse?.phi ?? profilePHI;

  // Run all sections in a report, concatenate outputs, store as full report HTML.
  // Also persists any in-memory edits to local storage before generating.
  const handleGenerateFullReport = async (report: AdminAssessmentReportDto, reportKey: string) => {
    if (!adminResponse) return;
    setIsGeneratingReport(prev => ({ ...prev, [reportKey]: true }));
    try {
      const sections = getAllReportSections(report);

      // Persist current in-memory edits to local storage before generating
      for (const section of sections) {
        const id = section.reportSectionId;
        if (editingScripts[id] !== undefined) {
          await persistReportScriptEdit(id, editingScripts[id], localDirtyFlags[id] ?? true);
        }
      }

      // Fresh Map per generation, shared across every section of THIS report — putData() calls
      // land here so they can be read back once generation completes (see reportDataOverrides).
      const dataOverrides = new Map<string, string>();

      // Precompute enableBarGraph/enableLineGraph trend chart data (see reportRenderer.ts's
      // parseTrendChartPreloads/buildTrendChartOverrides) — only when this report actually asks
      // for it, so reports that don't use trend charts never pay for history resolution.
      const trendRequests = parseTrendChartPreloads(report.reportPreloads);
      if (trendRequests.barGraphs.length > 0 || trendRequests.lineGraphs.length > 0) {
        let trendHistory: AdminResponseDto[];
        if (assessmentHistory) {
          // Client-profile flow — the caller already has the profile's embedded history on hand.
          trendHistory = resolveTrendHistoryFromEmbedded(adminResponse, assessmentHistory);
        } else {
          // On-device flow (WellVisitView/respondent sessions) — reuse the self-load effect's
          // fetch when available; otherwise fetch it here once and cache it for later reports on
          // this same page. Falls back to just the current response when there's truly no
          // profileId/storage to look anything up with, so the chart still renders one point
          // instead of nothing.
          if (!localProfileResponsesRef.current && storage && profileId) {
            const fetched = await new AdminResponseStorage(storage).getProfileResponses(profileId);
            localProfileResponsesRef.current = fetched.responses;
          }
          trendHistory = resolveTrendHistoryFromLocal(adminResponse, localProfileResponsesRef.current ?? [adminResponse]);
        }
        buildTrendChartOverrides(trendRequests, trendHistory).forEach((v, k) => dataOverrides.set(k, v));
      }

      const html = generateFullReportHtml(report, adminResponse, effectivePhi, editingScripts, dataOverrides);
      setFullReportOutputs(prev => ({ ...prev, [reportKey]: html }));

      // Merge in — don't replace wholesale — so with multiple reports shown at once
      // (reportsToShow.length > 1) a later report regenerating without setting some key another
      // report did set can't blank that key out.
      if (dataOverrides.size > 0) {
        setReportDataOverrides(prev => ({ ...prev, ...Object.fromEntries(dataOverrides) }));
      }
    } finally {
      setIsGeneratingReport(prev => ({ ...prev, [reportKey]: false }));
    }
  };

  // Continue button in script editor: re-generates the full report for the parent report
  const handleContinueSection = (
    _section: AdminReportSectionDto,
    report: AdminAssessmentReportDto,
    reportKey: string
  ) => {
    handleGenerateFullReport(report, reportKey);
  };

  // Update script, track dirty state, and persist to local storage
  const handleScriptChange = (
    reportSectionId: string,
    newScript: string,
    originalScript: string
  ) => {
    setEditingScripts(prev => ({ ...prev, [reportSectionId]: newScript }));
    setScriptDirtyFlags(prev => ({ ...prev, [reportSectionId]: newScript !== originalScript }));
    const isDirty = newScript !== originalScript;
    setLocalDirtyFlags(prev => ({ ...prev, [reportSectionId]: isDirty }));
    persistReportScriptEdit(reportSectionId, newScript, isDirty);
  };

  // Save report script to local storage then to the backend via report_logic_put
  const handleSaveScript = async (reportSectionId: string, reportSection: AdminReportSectionDto) => {
    const scriptToSave = editingScripts[reportSectionId] ?? reportSection.reportScript;

    setSavingScripts(prev => ({ ...prev, [reportSectionId]: true }));
    setSaveScriptResults(prev => ({ ...prev, [reportSectionId]: null }));

    // Always persist to local storage first (dirty=true until backend confirms)
    await persistReportScriptEdit(reportSectionId, scriptToSave, true);

    if (!apiService) {
      // No apiService — local-only save
      if (assessmentDefinition) {
        assessmentDefinition.reports?.forEach(r => {
          r.subReports?.forEach(sub => {
            sub.reportSections?.forEach(sec => {
              if (sec.reportSectionId === reportSectionId) sec.reportScript = scriptToSave;
            });
          });
        });
      }
      setScriptDirtyFlags(prev => ({ ...prev, [reportSectionId]: false }));
      setSaveScriptResults(prev => ({
        ...prev,
        [reportSectionId]: { success: true, message: 'Saved locally (no API service configured)' },
      }));
      setSavingScripts(prev => ({ ...prev, [reportSectionId]: false }));
      return;
    }

    try {
      // ReportPreloads moved up to the report level (AdminAssessmentReportDto.reportPreloads) —
      // no longer part of a section's own save payload.
      const payload = {
        ReportSections: [{
          DName: reportSection.dName,
          ReportSectionId: reportSectionId,
          ReportScript: scriptToSave,
        }],
      };

      const serviceRequest = {
        RequestType: 'report_logic_put',
        Payload: JSON.stringify(payload),
      };

      const response = isRespondentSession
        ? await withPractitionerAuth(apiService, storage, (practitionerStore) =>
            apiService.processServiceRequestWithRefresh(serviceRequest, 'content', {
              userStore: practitionerStore,
              storage,
            }))
        : await apiService.processServiceRequestWithRefresh(
            serviceRequest,
            'content',
            { userStore: userStoreHook, storage }
          );

      if (response?.success) {
        console.log('✅ ReportView: Report script saved to backend:', reportSectionId);

        // Update the in-memory assessment definition
        if (assessmentDefinition) {
          assessmentDefinition.reports?.forEach(r => {
            r.subReports?.forEach(sub => {
              sub.reportSections?.forEach(sec => {
                if (sec.reportSectionId === reportSectionId) sec.reportScript = scriptToSave;
              });
            });
          });
        }

        // Clear dirty flags — local storage and in-memory
        await clearReportScriptDirtyInStorage(reportSectionId);
        setLocalDirtyFlags(prev => ({ ...prev, [reportSectionId]: false }));
        setScriptDirtyFlags(prev => ({ ...prev, [reportSectionId]: false }));
        setSaveScriptResults(prev => ({
          ...prev,
          [reportSectionId]: { success: true, message: 'Script saved to backend successfully' },
        }));
      } else {
        console.error('❌ ReportView: Backend rejected report script save:', response);
        setSaveScriptResults(prev => ({
          ...prev,
          [reportSectionId]: {
            success: false,
            message: response?.message || 'Save failed — check console for details',
          },
        }));
      }
    } catch (error) {
      console.error('❌ ReportView: Error saving report script to backend:', error);
      setSaveScriptResults(prev => ({
        ...prev,
        [reportSectionId]: {
          success: false,
          message: 'Save error: ' + (error instanceof Error ? error.message : String(error)),
        },
      }));
    } finally {
      setSavingScripts(prev => ({ ...prev, [reportSectionId]: false }));
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getAssessmentDisplayName = (): string => {
    // Use AdminAssessmentDto.dName if available, otherwise fallback
    if (assessmentDefinition?.dName) {
      return assessmentDefinition.dName;
    }

    if (!adminResponse) return 'Assessment Report';

    // Fallback to mapping common survey IDs to friendly names
    const surveyNames: { [key: string]: string } = {
      '1987': 'PHQ-9 Depression Screening',
      '1988': 'GAD-7 Anxiety Screening', 
      '1989': 'DASS-21 Assessment',
      '1990': 'PSQ Parenting Stress',
    };

    if (surveyNames[adminResponse.recSurvey]) {
      return surveyNames[adminResponse.recSurvey];
    }

    if (adminResponse.recAlias && adminResponse.recAlias !== adminResponse.recSurvey) {
      return `${adminResponse.recAlias} Assessment`;
    }

    return `Assessment ${adminResponse.recSurvey}`;
  };

  if (loading) {
    return (
      <View style={styles.container}>
        {renderSkinHeader()}
        <View style={styles.questionCard}>
          <Text style={styles.title}>Loading Report...</Text>
        </View>
      </View>
    );
  }

  if (error || !adminResponse) {
    return (
      <View style={styles.container}>
        {renderSkinHeader()}
        <View style={styles.questionCard}>
          <Text style={styles.title}>Report Error</Text>
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error || 'Report not available'}</Text>
          </View>
          {onBack && (
            <View style={styles.questionFooter}>
              <View style={styles.questionFooterRow}>
                <View style={styles.questionFooterCol} />
                <View style={[styles.questionFooterCol, styles.questionFooterColCenter]}>
                  <TouchableOpacity onPress={onBack} style={styles.footerContinueButton}>
                    <Text style={styles.footerContinueButtonText}>{footerButtonLabel}</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.questionFooterCol} />
              </View>
            </View>
          )}
        </View>
      </View>
    );
  }

  // selectedReportId narrows the respondent-facing output cards to just one report (e.g.
  // AdminView's renderReport PreScript flag) — omitted, this shows every report, unchanged
  // from the default behavior every existing caller relies on. Keeps each report's ORIGINAL
  // index from assessmentDefinition.reports (not the filtered array's own index) because
  // fullReportOutputs/webViewHeights/the auto-generation effect are all keyed by that original
  // index (String(reportIndex) from the unfiltered assessmentDefinition.reports.forEach).
  const reportEntries = (assessmentDefinition?.reports ?? [])
    .map((report, originalIndex) => ({ report, originalIndex }));
  const reportsToShow = reportEntries
    .filter(({ report }) => !selectedReportId || report.reportId === selectedReportId);

  // ── TestDrive / demo "hub" report ─────────────────────────────────────────
  // When the single selected report carries hubReport preloads (see getReportHubConfig in
  // reportRenderer.ts), ReportView renders it as a container: the hub report's own HTML, one
  // nav button per child report ("View Client Report" / "View Clinician Report"), and a
  // continue button that stays disabled until each child has been opened. A child's "← Back"
  // returns to the hub. Completely inert for every report without those preloads — hubConfig
  // .enabled is false unless selectedReportId names a report with a truthy hubReport preload,
  // so the normal (single- and multi-report) render paths below are untouched.
  const selectedReport = selectedReportId
    ? (assessmentDefinition?.reports ?? []).find(r => r.reportId === selectedReportId)
    : undefined;
  const hubConfig = getReportHubConfig(selectedReport);
  const hubChildren = hubConfig.children.filter(c =>
    (assessmentDefinition?.reports ?? []).some(r => r.reportId === c.reportId)
  );
  const allHubChildrenViewed =
    hubChildren.length > 0 && hubChildren.every(c => viewedChildReportIds.has(c.reportId));
  const hubEntry = hubConfig.enabled
    ? reportEntries.find(e => e.report.reportId === selectedReportId)
    : undefined;
  const activeChildEntry = activeChildReportId
    ? reportEntries.find(e => e.report.reportId === activeChildReportId)
    : undefined;
  const markHubChildViewed = (reportId: string) => {
    setViewedChildReportIds(prev => {
      if (prev.has(reportId)) return prev;
      const next = new Set(prev);
      next.add(reportId);
      return next;
    });
  };

  // overrideRWidth — a report script's putData('overrideRWidth', '<px>') controls the page's
  // main card width, mirroring AdminView's overrideQWidth/getQuestionWidthOverride.
  const reportWidthOverrideRaw = reportDataOverrides['overrideRWidth'];
  const reportWidthOverrideParsed = reportWidthOverrideRaw !== undefined ? parseInt(reportWidthOverrideRaw, 10) : NaN;
  const reportWidthOverride = reportWidthOverrideRaw !== undefined && !Number.isNaN(reportWidthOverrideParsed)
    ? reportWidthOverrideParsed
    : undefined;

  // Single consolidated Debug Information panel — mirrors AdminView.tsx's renderDebugInfo:
  // one bordered panel titled "🐛 Debug Information", each fact grouped into its own
  // debugInfoSection with a title + a single debugInfoDataItem box (or debugInfoText when
  // there's nothing to show), rather than the several separately-styled sections this used to
  // render straight into the scroll content.
  const renderDebugInfo = () => {
    if (!localShowDebugInfo || !adminResponse) return null;

    return (
      <View style={styles.debugPanelContainer}>
        <Text style={styles.debugPanelTitle}>🐛 Debug Information</Text>

        {/* Assessment Responses */}
        <View style={styles.debugInfoSection}>
          <Text style={styles.debugInfoSectionTitle}>
            Assessment Responses ({adminResponse.responseData?.length || 0} items):
          </Text>
          <View style={styles.debugInfoDataItem}>
            {adminResponse.responseData && adminResponse.responseData.length > 0 ? (
              <Text style={styles.debugInfoDataValue}>
                {adminResponse.responseData.map(r => `${r.name}: ${r.value}`).join('\n')}
              </Text>
            ) : (
              <Text style={styles.debugInfoText}>No response data available</Text>
            )}
          </View>
        </View>

        {/* Report Data (putData) — everything a report script has putData()'d so far this
            session (e.g. overrideRWidth, or a scratch value like a base64 icon stashed once
            and re-read via getData() from later sections/reports). Long values (data URIs
            especially) are truncated here for readability — the full value is still exactly
            what getData() sees, this display just doesn't dump it all inline. */}
        <View style={styles.debugInfoSection}>
          <Text style={styles.debugInfoSectionTitle}>
            Report Data — putData()/getData() ({Object.keys(reportDataOverrides).length} items):
          </Text>
          <Text style={styles.debugInfoSubtitle}>💡 Set from report scripts with putData("name", "value"); read back with getData("name").</Text>
          <View style={styles.debugInfoDataItem}>
            {Object.keys(reportDataOverrides).length > 0 ? (
              <Text style={styles.debugInfoDataValue}>
                {Object.entries(reportDataOverrides)
                  .map(([k, v]) => `${k}: ${v.length > 80 ? `${v.slice(0, 80)}… (${v.length} chars)` : v}`)
                  .join('\n')}
              </Text>
            ) : (
              <Text style={styles.debugInfoText}>No report data set</Text>
            )}
          </View>
        </View>

        {/* PHI Data — reuses effectivePhi (adminResponse.phi ?? profilePHI, hoisted above
            handleGenerateFullReport) so this display and actual report generation's getPHI()
            can never drift apart the way they did before this was a shared value. */}
        <View style={styles.debugInfoSection}>
          <Text style={styles.debugInfoSectionTitle}>PHI Data:</Text>
          <View style={styles.debugInfoDataItem}>
            {effectivePhi ? (
              <Text style={styles.debugInfoDataValue}>
                {[
                  ['dname', effectivePhi.dname],
                  ['fname', effectivePhi.fname],
                  ['lname', effectivePhi.lname],
                  ['email', effectivePhi.email],
                  ['dob', effectivePhi.dob],
                  ['gender', effectivePhi.gender],
                  ...(effectivePhi.extra ?? []).map(e => [e.name, e.value]),
                ].map(([k, v]) => `${k}: ${v}`).join('\n')}
              </Text>
            ) : (
              <Text style={styles.debugInfoText}>No PHI data available</Text>
            )}
          </View>
        </View>

        {/* Report Preloads — scoped to the whole report (AdminAssessmentReportDto.reportPreloads),
            not an individual section; grouped here by report dName since one report's sections
            all share the same preload list. Iterates reportsToShow (not assessmentDefinition.
            reports directly) so this reflects only the report(s) actually being displayed —
            e.g. just the one selectedReportId names — rather than every report in the
            assessment, which previously made two differently-scoped reports sharing a display
            name look like duplicate/blank entries here. */}
        {reportsToShow.length > 0 && (
          <View style={styles.debugInfoSection}>
            <Text style={styles.debugInfoSectionTitle}>Report Preloads:</Text>
            <View style={styles.debugInfoDataItem}>
              {(() => {
                const allPreloads: { reportName: string; name: string; value: string; type?: string }[] = [];
                reportsToShow.forEach(({ report: r }) =>
                  r.reportPreloads?.forEach(p =>
                    allPreloads.push({ reportName: r.dName, name: p.name, value: p.value, type: p.type })
                  )
                );
                return allPreloads.length > 0 ? (
                  <Text style={styles.debugInfoDataValue}>
                    {allPreloads
                      .map(item => `[${item.reportName}] ${item.name}${item.type ? ` (${item.type})` : ''}: ${item.value}`)
                      .join('\n')}
                  </Text>
                ) : (
                  <Text style={styles.debugInfoText}>No preloads configured</Text>
                );
              })()}
            </View>
          </View>
        )}

        {/* Admin Record Info */}
        <View style={styles.debugInfoSection}>
          <Text style={styles.debugInfoSectionTitle}>Admin Record Info:</Text>
          <View style={styles.debugInfoDataItem}>
            <Text style={styles.debugInfoDataValue}>
              {[
                ['recId', adminResponse.recId],
                ['recSurvey', adminResponse.recSurvey],
                ['recAlias', adminResponse.recAlias],
                ['recStatus', adminResponse.recStatus],
                ['recUTM', adminResponse.recUTM],
                ['recDate', adminResponse.recDate],
                ['recPractice', adminResponse.recPractice],
                ['recProvider', adminResponse.recProvider],
                ['recDepartment', adminResponse.recDepartment],
                ['recSpecialty', adminResponse.recSpecialty],
                ['duration', adminResponse.duration != null ? String(adminResponse.duration) : undefined],
              ]
                .filter(([, v]) => v != null && v !== '')
                .map(([k, v]) => `${k}: ${v}`)
                .join('\n')}
            </Text>
          </View>
        </View>

        {/* Assessment Summary */}
        <View style={styles.debugInfoSection}>
          <Text style={styles.debugInfoSectionTitle}>Assessment Summary:</Text>
          <View style={styles.debugInfoDataItem}>
            <Text style={styles.debugInfoDataValue}>
              {([
                ['Assessment', getAssessmentDisplayName()],
                ['Completed', formatDate(adminResponse.recUTM)],
                ['Status', adminResponse.recStatus || 'Completed'],
                ['Record ID', adminResponse.recId],
                ...(assessmentDefinition ? [
                  ['Assessment ID', assessmentDefinition.surveyId],
                  ['Language', assessmentDefinition.languageTypeId],
                ] : []),
                ...(adminResponse.duration ? [['Duration', `${adminResponse.duration} minutes`]] : []),
              ] as [string, string][])
                .map(([k, v]) => `${k}: ${v}`)
                .join('\n')}
            </Text>
          </View>
        </View>

        {/* Additional Information */}
        <View style={styles.debugInfoSection}>
          <Text style={styles.debugInfoSectionTitle}>Additional Information:</Text>
          <View style={styles.debugInfoDataItem}>
            {(() => {
              const lines = ([
                ['Provider', adminResponse.recProvider],
                ['Department', adminResponse.recDepartment],
                ['Specialty', adminResponse.recSpecialty],
                ['Practice', adminResponse.recPractice],
                ['Date', adminResponse.recDate ? formatDate(adminResponse.recDate) : undefined],
              ] as [string, string | undefined][]).filter(([, v]) => v);
              return lines.length > 0 ? (
                <Text style={styles.debugInfoDataValue}>{lines.map(([k, v]) => `${k}: ${v}`).join('\n')}</Text>
              ) : (
                <Text style={styles.debugInfoText}>No additional information available</Text>
              );
            })()}
          </View>
        </View>
      </View>
    );
  };

  // "View" + PDF icon — opens this report's already-generated HTML as a PDF (same HTML the
  // inline view shows, incl. trend charts). Disabled until that HTML has been generated. Always
  // paired with the Back link (see renderBackOrViewNav) rather than shown on its own — a report
  // page always has a back destination when it has anything to view a PDF of.
  const renderPdfAction = (report: AdminAssessmentReportDto, originalIndex: number) => {
    if (!showPdfAction) {
      return null;
    }
    const html = fullReportOutputs[String(originalIndex)];
    return (
      <TouchableOpacity
        onPress={() => { if (html && onViewPdf) onViewPdf(html, assessmentDefinition?.dName || report.dName || 'Report'); }}
        disabled={!html}
        style={[styles.reportPdfAction, !html && styles.reportPdfActionDisabled]}
        accessibilityRole="button"
        accessibilityLabel="View report as PDF"
      >
        <Text style={styles.reportOutputBackText}>View</Text>
        {pdfIconSrc ? (
          <Image source={{ uri: pdfIconSrc }} style={styles.reportPdfIcon} resizeMode="contain" />
        ) : (
          <Text style={styles.reportPdfGlyph}>📄</Text>
        )}
      </TouchableOpacity>
    );
  };

  // One report's generated-HTML output — an auto-height WebView, or a placeholder until the
  // generation effect has produced HTML for it. Shared by the default report loop and the
  // hub-report screens so the WebView wiring lives in exactly one place.
  const renderReportOutput = (report: AdminAssessmentReportDto, originalIndex: number) => {
    const reportKey = String(originalIndex);
    const allSections = getAllSectionsForReport(report);
    return fullReportOutputs[reportKey] ? (
      <WebView
        source={{ html: wrapHtml(fullReportOutputs[reportKey]) }}
        style={[styles.reportWebView, { height: webViewHeights[reportKey] || 0 }]}
        scrollEnabled={false}
        injectedJavaScript={webViewAutoHeightJS}
        onMessage={(e) =>
          setWebViewHeights(prev => ({
            ...prev,
            [reportKey]: Number(e.nativeEvent.data),
          }))
        }
      />
    ) : (
      <Text style={styles.noReportPlaceholder}>
        {allSections.length > 0
          ? `${allSections.length} section${allSections.length !== 1 ? 's' : ''} ready — press Generate Report to produce output`
          : 'No report sections configured for this report'}
      </Text>
    );
  };

  // Footer button behavior. In hub mode: on a child screen it's a "← Back" to the hub; on the
  // hub screen it's the gated continue button (label/gate from the hub preloads). Otherwise
  // it's the existing single exit/continue button that calls onBack.
  const onHubChildScreen = hubConfig.enabled && !!activeChildEntry;
  const onHubRootScreen = hubConfig.enabled && !activeChildEntry;
  const footerLabel = onHubChildScreen
    ? '← Back'
    : onHubRootScreen
    ? hubConfig.continueLabel
    : footerButtonLabel;
  const footerDisabled = onHubRootScreen && hubConfig.requireAllViewed && !allHubChildrenViewed;
  const handleFooterPress = onHubChildScreen ? () => setActiveChildReportId(null) : onBack;

  // The one "← Back" a report page can have — a hub child screen (back to the hub root), or the
  // default view's first report when the caller gave onBack a real "go back" meaning (not the
  // /link-style "Continue" case, where onBack proceeds to completeUrl instead). Whichever report
  // it's attached to also carries the PDF action, paired as "← Back or View" — see
  // renderBackOrViewNav, rendered above the card rather than inside it.
  const backNav = hubConfig.enabled
    ? (activeChildEntry
      ? { report: activeChildEntry.report, originalIndex: activeChildEntry.originalIndex, onPress: () => setActiveChildReportId(null) }
      : null)
    : (reportsToShow.length > 0 && onBack && footerButtonLabel === 'Back'
      ? { report: reportsToShow[0].report, originalIndex: reportsToShow[0].originalIndex, onPress: onBack }
      : null);

  const renderBackOrViewNav = () => {
    if (!backNav) {
      return null;
    }
    const pdfAction = renderPdfAction(backNav.report, backNav.originalIndex);
    return (
      <View style={reportWidthOverride !== undefined
        ? [styles.reportTopNavRow, { maxWidth: reportWidthOverride }]
        : styles.reportTopNavRow}
      >
        <TouchableOpacity onPress={backNav.onPress} accessibilityRole="button" accessibilityLabel="Back">
          <Text style={styles.reportOutputBackText}>← Back</Text>
        </TouchableOpacity>
        {pdfAction ? (
          <>
            <Text style={styles.reportOutputBackText}>or</Text>
            {pdfAction}
          </>
        ) : null}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {renderSkinHeader()}
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {renderBackOrViewNav()}
        <View style={reportWidthOverride !== undefined
          ? [styles.questionCard, { maxWidth: reportWidthOverride }]
          : styles.questionCard}
        >

          {/* Generated report HTML — at the top of the card. Hub mode (a selected report
              carrying hubReport preloads) renders either the open child report or the hub
              screen (hub HTML + child nav buttons); otherwise the default one-per-report
              loop (every report, or just the one selectedReportId names). */}
          {hubConfig.enabled ? (
            activeChildEntry ? (
              <View key={`hub-child-${activeChildEntry.report.reportId}`}>
                {/* Back arrow (+ PDF action) now renders above the card — see
                    renderBackOrViewNav — the child report's own generated HTML carries its
                    title, so a bold chrome-level report name here would be redundant anyway. */}
                {renderReportOutput(activeChildEntry.report, activeChildEntry.originalIndex)}
              </View>
            ) : (
              <View key="hub-root">
                {hubEntry ? renderReportOutput(hubEntry.report, hubEntry.originalIndex) : null}
                <View style={styles.hubButtonRow}>
                  {hubChildren.map(child => {
                    const viewed = viewedChildReportIds.has(child.reportId);
                    return (
                      <View key={child.reportId} style={styles.hubButtonLine}>
                        <TouchableOpacity
                          onPress={() => {
                            markHubChildViewed(child.reportId);
                            setActiveChildReportId(child.reportId);
                          }}
                          onLayout={(e) => {
                            const w = e.nativeEvent.layout.width;
                            setHubButtonWidth((prev) => (prev === undefined || w > prev ? w : prev));
                          }}
                          style={[styles.hubNavButton, hubButtonWidth ? { width: hubButtonWidth } : null]}
                          accessibilityRole="button"
                        >
                          <Text style={styles.hubNavButtonText} numberOfLines={1}>
                            {/* Checkmark slot is always laid out (hidden until viewed) so the
                                button width doesn't jump when a report gets opened.
                                numberOfLines={1} keeps the label on one line — the onLayout
                                measurement then captures its true single-line width, which is
                                applied (as the max across all buttons) so they end up equal. */}
                            <Text style={viewed ? undefined : styles.hubNavButtonCheckHidden}>✓ </Text>
                            {child.label}
                          </Text>
                        </TouchableOpacity>
                        {child.instruction ? (
                          <Text style={styles.hubNavButtonInstruction}>{child.instruction}</Text>
                        ) : null}
                      </View>
                    );
                  })}
                </View>
              </View>
            )
          ) : reportsToShow.length > 0 ? (
            reportsToShow.map(({ report, originalIndex }, i) => {
              return (
                <View key={`output-${originalIndex}`}>
                  {i > 0 && <View style={styles.sectionDivider} />}
                  {/* Per-report title — shown whenever more than one report is on the page, so
                      they can be told apart. The Back link (formerly shown here instead of the
                      first report's title) now renders above the card — see
                      renderBackOrViewNav — so every report's title can show here unconditionally. */}
                  {report.dName && reportsToShow.length > 1 ? (
                    <Text style={styles.reportOutputTitle2}>{report.dName}</Text>
                  ) : null}
                  {renderReportOutput(report, originalIndex)}
                </View>
              );
            })
          ) : (
            <Text style={styles.noReportPlaceholder}>No reports configured for this assessment</Text>
          )}

          {/* Hub screen's centered footer message (hubFooter preload), just above the
              continue button — e.g. "Please view both reports to proceed", with a spacer
              row separating it from the child nav buttons above. */}
          {onHubRootScreen && hubConfig.footerText ? (
            <>
              <View style={styles.hubFooterSpacer} />
              <View style={styles.hubFooterSpacer} />
              <Text style={styles.hubFooterText}>{hubConfig.footerText}</Text>
            </>
          ) : null}

          {/* Footer — single exit/continue button, styled like AdminView's enableDone/
              Continue button. onBack drives whatever "done with this report" means to the
              caller (WellVisitView/ProGroupsWeb: navigate back; SecureAdministrationView's
              immediate-feedback screen: proceed to completeUrl) — footerButtonLabel names it.
              In hub mode the label/target/disabled state come from the hub screen vs child
              screen instead (see footerLabel/footerDisabled/handleFooterPress above). */}
          {handleFooterPress && (
            <View style={styles.questionFooter}>
              <View style={styles.questionFooterRow}>
                <View style={styles.questionFooterCol} />
                <View style={[styles.questionFooterCol, styles.questionFooterColCenter]}>
                  <TouchableOpacity
                    onPress={handleFooterPress}
                    disabled={footerDisabled}
                    style={[styles.footerContinueButton, footerDisabled ? styles.footerContinueButtonDisabled : null]}
                  >
                    <Text style={styles.footerContinueButtonText}>{footerLabel}</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.questionFooterCol} />
              </View>
            </View>
          )}

          {/* Breathing room under the hub screen's Next/Back button so it doesn't sit flush
              against the card's bottom edge. */}
          {hubConfig.enabled && handleFooterPress ? <View style={styles.hubFooterSpacer} /> : null}

      {/* Report Script Editor — only when enableEditReportLogic, and only for the report(s)
          reportsToShow narrows to (every report by default; just the selected one when a
          caller like AdminView's renderReport flow named one — previously this always looped
          every report in the assessment regardless of selectedReportId). */}
      {localEnableEditReportLogic && reportsToShow.map(({ report, originalIndex }) => {
        const reportKey = String(originalIndex);
        const flatSections = getAllSectionsWithSubReport(report);
        if (flatSections.length === 0) return null;

        return (
          <View key={`editor-${originalIndex}`} style={styles.reportEditorBlock}>
            <View style={styles.reportEditorBlockHeader}>
              <Text style={styles.reportEditorBlockTitle}>Edit — {report.dName || 'Report'}</Text>
              <TouchableOpacity
                style={[styles.continueButton, isGeneratingReport[reportKey] ? { opacity: 0.5 } : {}]}
                onPress={() => handleGenerateFullReport(report, reportKey)}
                disabled={!!isGeneratingReport[reportKey]}
              >
                <Text style={styles.continueButtonText}>
                  {isGeneratingReport[reportKey] ? 'Generating...' : 'Generate Report ▶️'}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.reportEditorGroupCard}>
              {flatSections.map(({ subReportName, section }, sectionIndex) => (
                <View key={section.reportSectionId}>
                  {sectionIndex > 0 && <View style={styles.sectionDivider} />}
                  <Text style={styles.sectionSubReportLabel}>{subReportName}</Text>
                  <Text style={styles.debugTitle}>{section.dName || 'Section'}</Text>

                  {/* Script textbox */}
                  <View style={styles.debugSection}>
                    <Text style={styles.debugSectionTitle}>
                      {'Report Script JavaScript'}
                      {scriptDirtyFlags[section.reportSectionId]
                        ? ' (Modified ⚠️)'
                        : localDirtyFlags[section.reportSectionId]
                        ? ' (Locally saved 💾)'
                        : ''}
                      {':'}
                    </Text>
                    <TextInput
                      style={styles.scriptEditor}
                      value={editingScripts[section.reportSectionId] ?? section.reportScript}
                      onChangeText={text => handleScriptChange(section.reportSectionId, text, section.reportScript)}
                      multiline
                      placeholder="No ReportScript defined for this section"
                      placeholderTextColor="#999"
                    />
                    {(scriptDirtyFlags[section.reportSectionId] || localDirtyFlags[section.reportSectionId]) && (
                      <Text style={styles.dirtyIndicator}>
                        {scriptDirtyFlags[section.reportSectionId]
                          ? '⚠️ Script modified — Generate Report to preview, Save to Backend to sync'
                          : '💾 Saved locally — press Save to Backend to sync to server'}
                      </Text>
                    )}
                  </View>

                  {/* Save to Backend button + result */}
                  <View style={styles.debugSection}>
                    <TouchableOpacity
                      style={[
                        styles.continueButton,
                        {
                          opacity: savingScripts[section.reportSectionId] ? 0.5 : 1,
                          backgroundColor: savingScripts[section.reportSectionId] ? '#666' : '#007AFF',
                        },
                      ]}
                      onPress={() => handleSaveScript(section.reportSectionId, section)}
                      disabled={!!savingScripts[section.reportSectionId]}
                    >
                      <Text style={styles.continueButtonText}>
                        {savingScripts[section.reportSectionId] ? 'Saving...' : '💾 Save to Backend'}
                      </Text>
                    </TouchableOpacity>

                    {saveScriptResults[section.reportSectionId] && (
                      <Text style={[
                        styles.dirtyIndicator,
                        { color: saveScriptResults[section.reportSectionId]?.success ? '#28a745' : '#dc3545' },
                      ]}>
                        {saveScriptResults[section.reportSectionId]?.success ? '✅' : '❌'}{' '}
                        {saveScriptResults[section.reportSectionId]?.message}
                      </Text>
                    )}
                  </View>
                </View>
              ))}

              {/* Script API hint — once at the bottom of the group */}
              <View style={styles.scriptHintBox}>
                <Text style={styles.scriptHintTitle}>💡 Script API</Text>
                <Text style={styles.scriptHintLine}>{'obuf += "…"'}{' '}<Text style={styles.scriptHintComment}>// append HTML to output</Text></Text>
                <Text style={styles.scriptHintLine}>{'getData("varName")'}{' '}<Text style={styles.scriptHintComment}>// response value by mvar name</Text></Text>
                <Text style={styles.scriptHintLine}>{'getAdmin("recUTM")'}{' '}<Text style={styles.scriptHintComment}>// AdminResponseDto field: recId/recUTM/recLang/recAssessment/recPractice/id/etc.</Text></Text>
                <Text style={styles.scriptHintLine}>{'putData("name", "value")'}{' '}<Text style={styles.scriptHintComment}>// set a report-scoped var — e.g. overrideRWidth to size the page</Text></Text>
                <Text style={styles.scriptHintLine}>{'renderChart({type:"bar", labels:[...], values:[...]})'}{' '}<Text style={styles.scriptHintComment}>// inline SVG bar/line chart; line supports series:[{'{'}label,values{'}'},...]</Text></Text>
                <Text style={styles.scriptHintLine}>{'getChartData("name")'}{' '}<Text style={styles.scriptHintComment}>// precomputed trend labels/values/series from enableBarGraph/enableLineGraph — e.g. getChartData("bgname1_labels")</Text></Text>
                <Text style={styles.scriptHintLine}>{'getPreload("name")'}{' '}<Text style={styles.scriptHintComment}>// section preload value</Text></Text>
                <Text style={styles.scriptHintLine}>{'getPHI("field")'}{' '}<Text style={styles.scriptHintComment}>// PHI field: dname fname lname dob gender email</Text></Text>
                <Text style={styles.scriptHintLine}>{'formatDate("dateStr")'}{' '}<Text style={styles.scriptHintComment}>// format ISO date string</Text></Text>
                <Text style={styles.scriptHintLine}>{'calculateScore(["v1","v2"])'}{' '}<Text style={styles.scriptHintComment}>// sum numeric response values</Text></Text>
              </View>
            </View>
          </View>
        );
      })}

      {renderDebugInfo()}

        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
    paddingBottom: 32,
  },
  cardColumn: {
    width: '100%',
    maxWidth: 650,
    paddingHorizontal: 16,
  },
  // White page matching AdminView's question-page container.
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  // Skin branding header — logo + background only, no menu/nav buttons. Copied verbatim
  // from AdminView.tsx's own skinHeaderShell/skinHeaderBackgroundImage/skinHeaderLogo so the
  // report page's header matches the assessment page's exactly.
  skinHeaderShell: {
    width: '100%',
    height: QUESTION_HEADER_HEIGHT,
    backgroundColor: 'transparent',
  },
  skinHeaderBackgroundImage: {
    width: '100%',
    height: QUESTION_HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  skinHeaderLogo: {
    height: QUESTION_HEADER_LOGO_HEIGHT,
    width: QUESTION_HEADER_LOGO_HEIGHT,
    marginLeft: 24,
  },
  // Single main card — copied from AdminView's questionCard so the report page reads as the
  // same rounded/bordered/shadowed panel the assessment's question pages use.
  questionCard: {
    width: '100%',
    maxWidth: 690,
    alignSelf: 'center',
    marginTop: 16,
    marginBottom: 16,
    borderRadius: 20,
    backgroundColor: '#f7f7f7',
    borderWidth: 1,
    borderColor: '#f7f7f7',
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 16,
    paddingBottom: 16,
    shadowColor: '#dbdbdb',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    overflow: 'hidden',
  },
  // Footer — single exit/continue button, copied from AdminView's questionFooter/
  // footerContinueButton (the same styling as its enableDone/Continue button).
  questionFooter: {
    paddingTop: 16,
    paddingBottom: 4,
  },
  questionFooterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
  },
  questionFooterCol: {
    flex: 1,
    alignItems: 'center',
  },
  questionFooterColCenter: {
    alignItems: 'center',
  },
  // Grey pill button — matches the prior PsychScan HTML report's button shape
  // (background #e5e5e5, border-radius 10, 1×2 #b7b7b7 shadow, 55px tall, ≥140 wide).
  // Shared by the hub child nav buttons and the footer continue/Next button.
  footerContinueButton: {
    minWidth: 140,
    height: 55,
    borderRadius: 10,
    backgroundColor: '#e5e5e5',
    shadowColor: '#b7b7b7',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 25,
  },
  footerContinueButtonText: {
    color: '#000000',
    fontWeight: '400',
    fontSize: 18,
  },
  // Hub screen's continue button while it's still gated (children not all opened).
  footerContinueButtonDisabled: {
    opacity: 0.45,
  },
  // Hub-report child nav buttons ("View Client Report" / "View Clinician Report"), stacked
  // below the hub report's own HTML. The block is centered; each line is [button][instruction].
  hubButtonRow: {
    marginTop: 24,
    gap: 16,
    alignSelf: 'center',
    maxWidth: '100%',
  },
  hubButtonLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  hubNavButton: {
    minWidth: 210,
    height: 55,
    borderRadius: 10,
    backgroundColor: '#e5e5e5',
    shadowColor: '#b7b7b7',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 25,
    // Never let the row's instruction text squeeze the button (which would wrap its label);
    // the instruction (flexShrink: 1) absorbs any overflow instead.
    flexShrink: 0,
  },
  // Arial across the hub chrome — matches the report body content (report scripts emit
  // <font face="Arial">) and the prior system's button/label styling.
  hubNavButtonText: {
    color: '#000000',
    fontWeight: '400',
    fontSize: 18,
    fontFamily: 'Arial',
  },
  // "✓ " prefix laid out but invisible until the child report has been viewed.
  hubNavButtonCheckHidden: {
    color: 'transparent',
  },
  // Instruction shown to the right of a hub nav button (hubChildReports 3rd field).
  hubNavButtonInstruction: {
    fontSize: 16,
    color: '#2c3e50',
    flexShrink: 1,
    fontFamily: 'Arial',
  },
  // Spacer row between the hub child nav buttons and the hubFooter message.
  hubFooterSpacer: {
    height: 32,
  },
  // Centered hub-screen message just above the continue button (hubFooter preload).
  hubFooterText: {
    fontSize: 16,
    color: '#2c3e50',
    textAlign: 'center',
    alignSelf: 'stretch',
    fontFamily: 'Arial',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 0,
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginRight: 12,
  },
  backButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
    flex: 1,
    marginBottom: 12,
  },
  section: {
    margin: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 12,
  },
  summaryCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  assessmentName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  summaryLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    fontWeight: '600',
    width: 120,
  },
  summaryValue: {
    fontSize: 14,
    color: '#2c3e50',
    flex: 1,
  },
  responseContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    padding: 16,
  },
  responseItem: {
    marginBottom: 4,
  },
  responseVariable: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
  },
  responseValue: {
    fontSize: 14,
    fontWeight: '400',
    color: '#34495e',
  },
  detailsCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  detailItem: {
    fontSize: 14,
    color: '#2c3e50',
    marginBottom: 8,
  },
  errorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 16,
    color: '#e74c3c',
    textAlign: 'center',
  },
  noDataContainer: {
    backgroundColor: '#ffffff',
    padding: 32,
    borderRadius: 12,
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  noDataText: {
    fontSize: 14,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  sectionDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 16,
  },
  reportCard: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  reportTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 8,
  },
  reportDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 16,
  },
  subReportContainer: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  subReportTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#34495e',
    marginBottom: 12,
  },
  reportSectionCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    marginBottom: 8,
  },
  reportSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  reportSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
    flex: 1,
  },
  generateButton: {
    backgroundColor: '#3498db',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  generateButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  preloadsInfo: {
    fontSize: 12,
    color: '#95a5a6',
    marginBottom: 8,
  },
  reportOutput: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#27ae60',
    marginTop: 8,
  },
  reportOutputTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#27ae60',
    marginBottom: 6,
  },
  reportOutputText: {
    fontSize: 14,
    color: '#2c3e50',
    lineHeight: 20,
  },
  scriptEditorContainer: {
    backgroundColor: '#f1c40f',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
    borderWidth: 2,
    borderColor: '#f39c12',
  },
  scriptEditorTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#d35400',
    marginBottom: 8,
  },
  scriptEditor: {
    backgroundColor: '#ffffff',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    fontFamily: 'monospace',
    minHeight: 264,
    borderWidth: 1,
    borderColor: '#bdc3c7',
    textAlignVertical: 'top',
  },
  scriptEditorButtons: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 8,
  },
  applyButton: {
    backgroundColor: '#27ae60',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    flex: 1,
  },
  applyButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  saveButton: {
    backgroundColor: '#3498db',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    flex: 1,
  },
  saveButtonDisabled: {
    backgroundColor: '#bdc3c7',
  },
  saveButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  cancelButton: {
    backgroundColor: '#95a5a6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  cancelButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  editScriptButton: {
    backgroundColor: '#e67e22',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  editScriptButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  debugText: {
    fontSize: 14,
    color: '#7f8c8d',
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#e67e22',
  },
  // Card 1: Report Output
  reportOutputCard: {
    backgroundColor: '#ffffff',
    marginTop: 12,
    marginBottom: 4,
    padding: 12,
    width: '100%',
    alignSelf: 'stretch',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e1e8ed',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  reportOutputHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  reportOutputHeaderLeft: {
    flex: 1,
    marginRight: 12,
  },
  reportOutputTitle2: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  // Back navigation (+ the skin's PDF action, joined by "or") — rendered above the card rather
  // than inside it, so it reads as page chrome rather than part of the report content. Shares
  // the card's own max width/centering/side padding (see renderBackOrViewNav) so "← Back" lines
  // up with the card's left edge.
  reportTopNavRow: {
    width: '100%',
    maxWidth: 690,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    marginBottom: 8,
    paddingHorizontal: 20,
  },
  // "View" + PDF icon — same plain text as the Back link, icon spaced out from the label.
  reportPdfAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  reportPdfActionDisabled: {
    opacity: 0.4,
  },
  reportPdfIcon: {
    width: 22,
    height: 22,
  },
  reportPdfGlyph: {
    fontSize: 18,
  },
  // Deliberately not bold — the report's own generated header already carries visual weight,
  // and a bold "← Back" here competed with it for attention (reported as "pulls your eye
  // away"). Plain text also drops the redundant report name in favor of just "Back".
  reportOutputBackText: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#2c3e50',
  },
  reportTargetType: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 2,
  },
  reportHtmlOutput: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#27ae60',
  },
  reportHtmlText: {
    fontSize: 14,
    color: '#2c3e50',
    lineHeight: 22,
  },
  reportWebView: {
    width: '100%',
    backgroundColor: 'transparent',
  },
  noReportPlaceholder: {
    fontSize: 14,
    color: '#95a5a6',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 16,
  },
  // Card 2: Script Editor block
  reportEditorBlock: {
    marginHorizontal: 0,
    marginTop: 16,
    marginBottom: 4,
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
  },
  reportEditorBlockHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  reportEditorBlockTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2c3e50', // dark text — this card now sits on the white questionCard, not a dark page
    flex: 1,
    marginRight: 8,
  },
  reportEditorGroupCard: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#f0c87a',
    padding: 12,
    marginBottom: 8,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: '#f0c87a',
    marginVertical: 12,
  },
  sectionSubReportLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#95a5a6',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  debugContainer: {
    backgroundColor: '#fef9e7',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: '#f39c12',
  },
  pageLogicHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  debugTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#d35400',
    flex: 1,
    marginRight: 8,
  },
  continueButton: {
    backgroundColor: '#28a745',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
  },
  continueButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  debugSection: {
    marginTop: 10,
  },
  debugSectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#7f8c8d',
    marginBottom: 4,
  },
  // Debug Information panel — copied verbatim from AdminView.tsx's own
  // debugContainer/debugTitle/debugSection/debugSectionTitle/debugText/debugDataItem/
  // debugDataValue so this panel matches AdminView's layout exactly. Named debugInfo* /
  // debugPanel* (not reusing debugSection/debugSectionTitle/debugTitle above) because those
  // names are already taken by the differently-styled Report Script Editor cards above.
  debugPanelContainer: {
    backgroundColor: '#f8f9fa',
    borderTopWidth: 2,
    borderTopColor: '#e9ecef',
    padding: 12,
    marginTop: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  debugPanelTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: 8,
    textAlign: 'center',
  },
  debugInfoSection: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  debugInfoSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6c757d',
    marginBottom: 4,
  },
  // Mirrors AdminView.tsx's debugSubtitle — a smaller italic hint line under a section title.
  debugInfoSubtitle: {
    fontSize: 12,
    fontStyle: 'italic',
    color: '#7f8c8d',
    marginBottom: 8,
  },
  debugInfoText: {
    fontSize: 12,
    color: '#495057',
    marginLeft: 8,
    marginBottom: 2,
  },
  debugInfoDataItem: {
    backgroundColor: '#ffffff',
    padding: 8,
    marginLeft: 8,
    marginBottom: 4,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: '#007bff',
  },
  debugInfoDataValue: {
    fontSize: 11,
    color: '#007bff',
    marginTop: 2,
  },
  dirtyIndicator: {
    fontSize: 12,
    color: '#e67e22',
    marginTop: 4,
  },
  scriptHintBox: {
    marginTop: 10,
    backgroundColor: '#eef4fb',
    borderRadius: 6,
    padding: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#3498db',
  },
  scriptHintTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#2471a3',
    marginBottom: 4,
  },
  scriptHintLine: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#1a3a4e',
    marginBottom: 2,
  },
  scriptHintComment: {
    color: '#7f8c8d',
    fontStyle: 'italic',
  },
});